import { ArrayKeyValueObject, KeyValueObject } from "@/UI/Interfaces/IBaseUI";
import { ICriteriaFieldConfig, ICriteriaFieldValue } from "@/UI/Interfaces/IFieldConfig";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BaseMethods from "AlignBaseUI/BaseMethods";
import produce from "immer";

interface IColumns {
  filter: boolean;
  objectno: string;
  widgetobjecttypeno: string;
  helpobjectno: string;
}

export interface IHelpHeader {
  ColumnText: string;
  ColumnID: string;
  controltype: string;
  fielddataformatno: string;
  ColumnWidth: number;
  ishidden: boolean;
  hyperlinktransactioncode: string;
}

export interface IObjects {
  widgetno: string;
  widgetstxt: string;
  widgettype: string;
  widgetcontroltype: string;
  layout: string;
  isinsight: boolean;
  widgeticon: string;
  widgetcolor: string;
  isdashboard: boolean;
  widgetgroupno: string;
  widgetgrouplabel: string;
  widgetprocesscode: string;
  data: ArrayKeyValueObject;
  columns: Array<IColumns>;
  Header: Array<IHelpHeader>;
}

export interface IWidget {
  widgetgroupno: string,
  criteria: Array<ICriteriaFieldConfig>;
  FICriteria: Array<ICriteriaFieldConfig>;
  modelValue: KeyValueObject<ICriteriaFieldValue>;
  layout: Array<ILayout>;
  objects: Array<IObjects>;
  objectsLoaded: boolean;
}

export interface ILayout {
  layout: string;
  insightarea: string;
  widgetgroupno: string;
}

export interface IWidgetGroups {
  widgetgroupno: string,
  widgetgroupstxt: string
}

interface IDashboard {
  activeWidget: IWidget;
  widgetGroups: Array<IWidgetGroups>,
  isGroupsLoaded: boolean
}

const InitialState: IDashboard = {
  activeWidget: {
    widgetgroupno: "",
    criteria: [],
    FICriteria: [],
    modelValue: {},
    layout: [],
    objects: [],
    objectsLoaded: false
  },
  widgetGroups: [],
  isGroupsLoaded: false
}

const DashboardSlice = createSlice({
  name: "dashboard",
  initialState: InitialState,
  reducers: {
    saveUserDashboardLayout(state, action: PayloadAction<{ widgetgroupno: string, layout: string, insightarea: string }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.activeWidget.layout.push({
          widgetgroupno: payload.widgetgroupno,
          layout: payload.layout,
          insightarea: payload.insightarea
        });
      });
    },
    getDashboardBasicData() { },
    setDashboardBasicData(state, action: PayloadAction<{ widgetGroups: Array<IWidgetGroups> }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.widgetGroups = payload.widgetGroups;
        draftState.isGroupsLoaded = true;
      });
    },
    loadWidgetGroup(state, action: PayloadAction<string>) { },
    setWidgetGroup(state, action: PayloadAction<{ objects: Array<IObjects> }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.activeWidget.objectsLoaded = true;
        draftState.activeWidget.objects = payload.objects;
      });
    },
    loadWidgetCriteria(state, action: PayloadAction<string>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.activeWidget.widgetgroupno = payload;
        draftState.activeWidget.objectsLoaded = false;
      });
    },
    setWidgetCriteria(state, action: PayloadAction<{ criteria: Array<ICriteriaFieldConfig>, FICriteria: Array<ICriteriaFieldConfig>, layout: Array<ILayout> }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        let modelValue: IWidget["modelValue"] = {};
        const criteria = payload.criteria.filter(x => payload.FICriteria.find(y => y.fieldid == x.fieldid) == null);
        const FICriteria = payload.FICriteria.map(x => ({ ...x, filteroption: true, multiplevalue: true }));

        [...criteria, ...FICriteria].map(x => {
          let value = null;

          if (BaseMethods.getControlType(x.controltypeno) == "checkbox")
            value = x.defaultvalue == "1" ? true : false;
          else if (BaseMethods.getControlType(x.controltypeno) == "number")
            value = x.defaultvalue ? Number(x.defaultvalue) : 0;
          else if (BaseMethods.getControlType(x.controltypeno) == "date") {
            if (x.validatemandatory) {
              value = new Date();
              value.setHours(0, 0, 0, 0);
              value = `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}T00:00:00`;
            }
          }
          else
            value = x.defaultvalue || "";

          const isDimension = FICriteria.find(y => y.fieldid == x.fieldid) != null;

          modelValue[x.fieldid] = {
            fromValue: value,
            toValue: "",
            isDimension,
            filterType: "=",
            caseSensitive: false
          };

        });

        draftState.activeWidget.criteria = payload.criteria;
        draftState.activeWidget.FICriteria = payload.FICriteria;
        draftState.activeWidget.layout = payload.layout;
        draftState.activeWidget.modelValue = modelValue;
      });
    },
    setWidgetModelValue(state, action: PayloadAction<{ fieldId: string, newValue: string, toValue: boolean }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        if (payload.toValue) {
          draftState.activeWidget.modelValue[payload.fieldId].toValue = payload.newValue;
        }
        else {
          draftState.activeWidget.modelValue[payload.fieldId].fromValue = payload.newValue;
        }
      });

    },
    clearAllModel(state) {
      return produce(state, (draftState) => {
        const fieldIds = Object.keys(draftState.activeWidget.modelValue).filter(x => x != "period");
        fieldIds.forEach(x => {
          const criteriaConf = draftState.activeWidget.criteria.find(y => y.fieldid == x) as ICriteriaFieldConfig;
          let value = null;

          if (BaseMethods.getControlType(criteriaConf.controltypeno) == "checkbox")
            value = criteriaConf.defaultvalue == "1" ? true : false;
          else if (BaseMethods.getControlType(criteriaConf.controltypeno) == "number")
            value = criteriaConf.defaultvalue ? Number(criteriaConf.defaultvalue) : 0;
          else if (BaseMethods.getControlType(criteriaConf.controltypeno) == "date") {
            if (criteriaConf.validatemandatory) {
              value = new Date();
              value.setHours(0, 0, 0, 0);
              value = `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}T00:00:00`;
            }
          }
          else
            value = criteriaConf.defaultvalue || "";

          draftState.activeWidget.modelValue[x].fromValue = "";
          draftState.activeWidget.modelValue[x].toValue = "";
        })
      });
    },
    setFieldFilter(state, action: PayloadAction<{ fieldId: string, filterType: string, caseSensitive: boolean }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        draftState.activeWidget.modelValue[payload.fieldId].filterType = payload.filterType;
        draftState.activeWidget.modelValue[payload.fieldId].caseSensitive = payload.caseSensitive;
      });

    },
  },
});

export const { saveUserDashboardLayout, getDashboardBasicData, setDashboardBasicData, loadWidgetGroup, setWidgetGroup, loadWidgetCriteria, setWidgetCriteria, setWidgetModelValue, clearAllModel, setFieldFilter } = DashboardSlice.actions;

export default DashboardSlice.reducer;
